// The agent chat's context and thread mapping.
//
// The context is a real record the person chose — an Opportunity or a Job — not a pathname
// inference. It travels with every ask as the existing `[on screen: …]` line, so the model and
// her tools see the same thing the header shows.

import type { HistoryTurn } from './emma-chat';

export type ContextKind = 'opportunity' | 'job';

export type SelectedContext = {
  kind: ContextKind;
  ref: string;
  label: string;
  /** The linked Proposal, when one exists. `read_proposal` takes this UUID directly. */
  proposalId?: string | null;
  proposalTitle?: string | null;
};

/** The record a stored conversation was last pinned to, read from its own words. */
export function contextFromHistory(history: HistoryTurn[]): SelectedContext | null {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const match = /\[on screen: (opportunity|job) ([A-Z0-9-]+)(?: · proposal ([0-9a-f-]{36}))?\]/i.exec(history[i]!.message);
    if (match) return {
      kind: match[1]!.toLowerCase() as ContextKind,
      ref: match[2]!,
      label: '',
      proposalId: match[3] ?? null,
    };
  }
  return null;
}

export type ChatTurn = {
  role: 'you' | 'emma';
  text: string;
  steps?: string[];
  model?: string | null;
  error?: string;
};

export function contextLine(context: SelectedContext | null): string {
  if (!context) return '';
  const base = `[on screen: ${context.kind} ${context.ref}`;
  return context.proposalId ? `${base} · proposal ${context.proposalId}]` : `${base}]`;
}

export function contextLabel(context: SelectedContext | null): string {
  if (!context) return 'No context';
  return `${context.kind === 'opportunity' ? 'Opportunity' : 'Job'} ${context.ref}${context.label ? ` · ${context.label}` : ''}`;
}

/** One history row is one turn: his words, then hers. An interrupted question still shows. */
export function turnsFromHistory(history: HistoryTurn[]): ChatTurn[] {
  const out: ChatTurn[] = [];
  for (const h of history) {
    const clean = h.message.replace(/\n\n\[on screen: [^\]]+\]\s*$/, '');
    if (clean.trim()) out.push({ role: 'you', text: clean });
    if (h.reply.trim() || !h.message.trim()) out.push({ role: 'emma', text: h.reply, model: h.model });
  }
  return out;
}

/** The ask the server receives: the person's words, then the pinned record. */
export function messageWithContext(text: string, context: SelectedContext | null): string {
  const line = contextLine(context);
  return line ? `${text.trim()}\n\n${line}` : text.trim();
}
